import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-searchable-dropdown',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="form-floating searchable-dropdown position-relative">
      <input
        [attr.id]="id"
        [disabled]="disabled"
        [placeholder]="placeholder"
        [class.is-invalid]="invalid"
        class="form-control focus-ring focus-ring-light"
        type="text"
        autocomplete="off"
        name="search-no-autofill"
        [value]="query"
        (focus)="open()"
        (input)="onInput($event)"
        (blur)="onBlur()"
      />

      <label class="form-label" [for]="id">{{label}}</label>


      @if (isOpen && !disabled) {
      <div class="dropdown-menu d-block w-100 mt-1 p-0 shadow-sm searchable-dropdown-menu">
        @if (filteredItems.length === 0) {
        <button class="dropdown-item text-muted small py-2" type="button" disabled>
          Sin resultados
        </button>
        } @else {
        @for (item of filteredItems; track item[bindValue]) {
        <button class="dropdown-item py-2" type="button" (mousedown)="selectItem(item)">
          {{ item[bindLabel] }}
        </button>
        }
        }
      </div>
      }
    </div>
  `,
  styles: [`
    .searchable-dropdown-menu {
      max-height: 240px;
      overflow: auto;
      z-index: 1080;
    }
  `],
})
export class SearchableDropdownComponent implements OnChanges {
  @Input() id = '';
  @Input() items: any[] = [];
  @Input() bindLabel = 'label';
  @Input() bindValue = 'value';
  @Input() value: any = null;
  @Input() placeholder = 'Buscar...';
  @Input() disabled = false;
  @Input() invalid = false;
  @Input() label = ''

  @Output() valueChange = new EventEmitter<any>();
  @Output() blurred = new EventEmitter<void>();

  query = '';
  isOpen = false;
  private isSelectingWithMouse = false;

  constructor(private readonly el: ElementRef<HTMLElement>) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] || changes['items']) {
      const selected = this.items.find((item) => item?.[this.bindValue] === this.value);
      this.query = selected ? String(selected[this.bindLabel] ?? '') : '';
    }
  }

  get filteredItems(): any[] {
    const term = this.normalize(this.query);
    if (!term) return this.items;
    return this.items.filter((item) =>
      this.normalize(String(item?.[this.bindLabel] ?? '')).includes(term),
    );
  }

  open(): void {
    this.isOpen = true;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.query = target.value;
    this.isOpen = true;
  }

  selectItem(item: any): void {
    this.isSelectingWithMouse = true;
    this.valueChange.emit(item?.[this.bindValue] ?? null);
    this.query = String(item?.[this.bindLabel] ?? '');
    this.isOpen = false;
  }

  onBlur(): void {
    setTimeout(() => {
      this.blurred.emit();
      if (this.isSelectingWithMouse) {
        this.isSelectingWithMouse = false;
        return;
      }
      this.isOpen = false;
      const exact = this.items.find(
        (item) => this.normalize(String(item?.[this.bindLabel] ?? '')) === this.normalize(this.query),
      );
      if (!exact && this.query.trim() === '') {
        this.valueChange.emit(null);
      } else if (!exact) {
        const selected = this.items.find((item) => item?.[this.bindValue] === this.value);
        this.query = selected ? String(selected[this.bindLabel] ?? '') : '';
      }
    }, 120);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  private normalize(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }
}

